<template>
  <div class="demo-layout">
    <DemoBanner variant="embed" />
    <header class="demo-header">
      <span class="demo-brand">
        <span class="demo-brand-mark" aria-hidden="true">H</span>
        HudStore
      </span>
      <span class="demo-tag">{{ t('demo.banner.label') }}</span>
    </header>

    <div class="demo-content">
      <slot />
    </div>

    <AppDock demo />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'
import AppDock from './AppDock.vue'
import DemoBanner from './DemoBanner.vue'
import { useI18n } from '@/composables/useI18n'
import { useDemoTour } from '@/composables/useDemoTour'

const { t } = useI18n()
const { startDemoTour, stopDemoTour } = useDemoTour()

// Kick off the guided tour automatically once the shell + dock are in the DOM.
// Short delay lets the landing view (kanban) and dock paint so anchored steps
// resolve. Runs once per embed session (layout mounts a single time).
let tourTimer: ReturnType<typeof setTimeout> | null = null
onMounted(() => {
  tourTimer = setTimeout(() => startDemoTour(t as unknown as (key: string) => string), 800)
})
onBeforeUnmount(() => {
  if (tourTimer) clearTimeout(tourTimer)
  stopDemoTour()
})
</script>

<style scoped>
.demo-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--app-surface);
}
.demo-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--app-border, rgba(0, 0, 0, 0.08));
}
.demo-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--app-text, inherit);
}
.demo-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: #4A1622;
  color: #fff;
  border-radius: 3px;
  font-size: 0.85rem;
}
.demo-tag {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.6;
}
.demo-content {
  flex: 1;
  padding: var(--app-spacing-lg, 1.5rem);
  padding-bottom: 6.5rem; /* clearance for dock */
  overflow-y: auto;
}
@media (max-width: 768px) {
  .demo-content { padding: var(--app-spacing-md, 1rem); padding-bottom: 6.5rem; }
}
</style>
